import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { api, fileUrl, BACKEND } from "../lib/api";
import { setHead, parseSchema, prepareHtml } from "../lib/head";
import { useSite } from "../context/SiteContext";
import { ShareButtons } from "../components/site/ShareButtons";
import { Filigree } from "../components/site/Brand";
import { PostCard } from "./BlogList";

const buildSchema = (p, url, image) => {
  const s = p.seo || {};
  const custom = parseSchema(s.schema_json);
  if (s.schema_type === "None") return custom;
  const base = {
    "@context": "https://schema.org", "@type": p.kind === "page" ? "WebPage" : s.schema_type || "Article",
    headline: s.meta_title || p.title, description: s.meta_description || p.excerpt, url,
    datePublished: p.published_at, dateModified: p.updated_at, inLanguage: p.language === "hi" ? "hi-IN" : "en-IN",
    author: { "@type": "Organization", name: p.author || "DaddyAnna247" },
    publisher: { "@type": "Organization", name: "DaddyAnna247" },
    ...(image ? { image } : {}), ...(s.focus_keyword ? { keywords: s.focus_keyword } : {}),
  };
  const crumbs = {
    "@context": "https://schema.org", "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: window.location.origin + "/" },
      ...(p.kind === "blog" ? [{ "@type": "ListItem", position: 2, name: "Blog", item: window.location.origin + "/blog" }] : []),
      { "@type": "ListItem", position: p.kind === "blog" ? 3 : 2, name: p.title, item: url },
    ],
  };
  return [base, crumbs, ...custom];
};

export const KeyedPostView = ({ kind }) => {
  const { slug } = useParams();
  return <PostView key={`${kind}-${slug}`} kind={kind} />;
};

export default function PostView({ kind }) {
  const { slug } = useParams();
  const { site } = useSite();
  const [data, setData] = useState(null);
  const [missing, setMissing] = useState(false);
  const perf = site?.performance;

  useEffect(() => {
    setData(null);
    setMissing(false);
    api.get(`/public/posts/${kind}/${slug}`).then(({ data }) => setData(data)).catch(() => setMissing(true));
  }, [kind, slug]);

  useEffect(() => {
    if (!data) return;
    const p = data.post;
    const s = p.seo || {};
    const url = s.canonical || `${window.location.origin}/${kind === "blog" ? "blog" : "p"}/${p.slug}`;
    const image = s.og_image ? fileUrl(s.og_image, perf?.cdn_base_url) : kind === "blog" ? `${BACKEND}/api/og/blog/${p.slug}.jpg` : fileUrl(p.featured_image, perf?.cdn_base_url);
    setHead({ title: s.meta_title || p.title, description: s.meta_description || p.excerpt, canonical: url, image,
      type: "article", locale: p.language === "hi" ? "hi_IN" : "en_IN", robots: s.noindex ? "noindex, nofollow" : "index, follow",
      schema: buildSchema(p, url, image) });
  }, [data, kind, perf]);

  if (missing) return (
    <section className="mx-auto max-w-3xl px-4 py-32 text-center" data-testid="post-not-found">
      <h1 className="font-serif text-3xl text-white">Page not found</h1>
      <Link to="/" className="btn-gold mt-8">Go Home</Link>
    </section>
  );
  if (!data) return <p className="px-4 py-32 text-center text-zinc-500">Loading…</p>;

  const p = data.post;
  const url = window.location.href.split("#")[0];
  const shareUrl = `${BACKEND}/api/share/blog/${p.slug}`;
  return (
    <article className="mx-auto max-w-3xl px-4 py-14 sm:px-6 md:py-20" data-testid="post-view">
      {kind === "blog" && <Link to="/blog" data-testid="back-to-blog" className="mb-8 inline-flex items-center gap-2 text-xs uppercase tracking-widest text-[#D4AF37]"><ArrowLeft size={14} /> Blog</Link>}
      <h1 className="font-serif text-3xl font-bold leading-tight text-white sm:text-4xl lg:text-5xl" data-testid="post-title">{p.title}</h1>
      <div className="mt-5 flex flex-wrap items-center gap-4 text-xs uppercase tracking-widest text-zinc-500">
        {kind === "blog" && <span>{new Date(p.published_at || p.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" })}</span>}
        {kind === "blog" && <span data-testid="post-read-time">{p.reading_minutes || 1} min read</span>}
        {p.tags?.map((t) => <span key={t} className="rounded-full border border-[#D4AF37]/30 px-3 py-1 text-[#D4AF37]">{t}</span>)}
      </div>
      <div className="mt-5"><Filigree /></div>
      {p.featured_image && (
        <img src={fileUrl(p.featured_image, perf?.cdn_base_url)} alt={p.featured_alt || p.title} data-testid="post-featured-image"
          className="mt-8 aspect-[16/9] w-full rounded-2xl border border-[#D4AF37]/25 object-cover" />
      )}
      {kind === "blog" && <div className="mt-8"><ShareButtons url={url} shareUrl={shareUrl} title={p.title} /></div>}
      <div className="prose-gold mt-10" data-testid="post-content" dangerouslySetInnerHTML={{ __html: prepareHtml(p.content_html, perf, site?.contacts) }} />
      {kind === "blog" && <div className="mt-12 border-t border-[#D4AF37]/20 pt-8"><ShareButtons url={url} shareUrl={shareUrl} title={p.title} /></div>}
      {kind === "blog" && data.related.length > 0 && (
        <section className="mt-16 border-t border-[#D4AF37]/20 pt-12 lg:-mx-24" data-testid="related-posts">
          <p className="text-xs uppercase tracking-[0.3em] text-[#D4AF37]">Keep reading</p>
          <h2 className="mb-8 mt-2 font-serif text-2xl text-white">
            {p.category && p.category !== "general" && data.related.some((r) => r.category === p.category) ? `Related ${p.category === "casino" ? "Casino" : "Cricket"} Articles` : "Related Articles"}
          </h2>
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {data.related.slice(0, 3).map((r) => <PostCard key={r.id} p={r} cdn={perf?.cdn_base_url} lazy />)}
          </div>
        </section>
      )}
    </article>
  );
}
